import { FlatToken } from '~/app/accounting/useAccountingTokenPositions'
import { isStablecoin } from '~/utils/addresses'
import { getBigNumberFromNumber } from '~/utils/numbers'
import { getVaultAssetData } from '~/utils/vault'

type Sides = {
  start: FlatToken | undefined
  end: FlatToken | undefined
}

export type NetChangeBreakdown = {
  tokenChange: bigint
  flowUsd: number | undefined
  gainLossUsd: number | undefined
}

// Underlying-asset registry entry when amounts were converted to $1
// underlying units (earn rows); undefined otherwise.
type Underlying = ReturnType<typeof getVaultAssetData>

export type TokenNetChange = Sides & {
  underlying: Underlying
} & NetChangeBreakdown

// Converting or decomposing with a missing price would mix units.
const hasUnpricedSide = ({ start, end }: Sides) =>
  (start && start.usdValue == null) || (end && end.usdValue == null)

// Measure earn (vault) rows in underlying units derived from each boundary's
// USD value — the share count stays flat while yield accrues, so this makes
// yield read as an inflow. Only valid for $1 underlyings, hence isStablecoin.
export const toUnderlyingAssetTokens = ({
  start,
  end,
}: Sides): Sides & { underlying: Underlying } => {
  const token = end ?? start
  if (!token) return { start, end, underlying: undefined }

  const underlying = getVaultAssetData(token.chainId, token.address)
  if (!underlying || !isStablecoin(underlying.asset, token.chainId)) {
    return { start, end, underlying: undefined }
  }

  if (hasUnpricedSide({ start, end })) {
    return { start, end, underlying: undefined }
  }

  // The address stays the vault token for identity; `underlying` tells cells
  // the amounts are no longer share counts.
  const convert = (side: FlatToken | undefined): FlatToken | undefined =>
    side && {
      ...side,
      symbol: underlying.symbol,
      rawBalance: getBigNumberFromNumber(
        side.usdValue!,
        underlying.decimals,
      ).toString(),
    }

  return { start: convert(start), end: convert(end), underlying }
}

// Split net change (endUsd - startUsd) into net flow (token amount change
// valued at the period-end price — a disclosed simplification that needs only
// the two snapshots) and gain/loss (the residual, so the parts sum exactly).
export const buildNetChangeBreakdown = ({
  start,
  end,
}: Sides): NetChangeBreakdown => {
  const startRaw = BigInt(start?.rawBalance ?? '0')
  const endRaw = BigInt(end?.rawBalance ?? '0')
  const tokenChange = endRaw - startRaw

  // Unpriced on a present side → USD decomposition unavailable.
  if (hasUnpricedSide({ start, end })) {
    return { tokenChange, flowUsd: undefined, gainLossUsd: undefined }
  }

  const startUsd = start?.usdValue ?? 0
  const endUsd = end?.usdValue ?? 0

  // Opened or fully exited during the period: the whole change is flow.
  if (!start) return { tokenChange, flowUsd: endUsd, gainLossUsd: 0 }
  if (!end) return { tokenChange, flowUsd: -startUsd, gainLossUsd: 0 }

  const endPricePerRawUnit = endUsd / Number(endRaw)
  // Guard a 0/0 price (upstream filters zero balances).
  if (!Number.isFinite(endPricePerRawUnit)) {
    return { tokenChange, flowUsd: undefined, gainLossUsd: undefined }
  }

  const flowUsd = Number(tokenChange) * endPricePerRawUnit
  const gainLossUsd = endUsd - startUsd - flowUsd
  // Float dust in the residual (~eps × endUsd) would render as "<$0.01"; the
  // clamp scales with magnitude so multi-million positions don't leak dust.
  const dust = Math.max(1e-9, endUsd * 1e-11)
  return {
    tokenChange,
    flowUsd,
    gainLossUsd: Math.abs(gainLossUsd) < dust ? 0 : gainLossUsd,
  }
}

// Convert earn rows to underlying units, then decompose. A $1-pegged
// underlying can't move in price, so its whole change is flow — set exactly
// rather than left to the quantized rawBalances, whose rounding residual
// (~1e-7) would otherwise leak past the dust clamp as a phantom "±<$0.01".
export const buildTokenNetChange = (sides: Sides): TokenNetChange => {
  const converted = toUnderlyingAssetTokens(sides)
  const breakdown = buildNetChangeBreakdown(converted)
  if (!converted.underlying) return { ...converted, ...breakdown }
  return {
    ...converted,
    ...breakdown,
    flowUsd: (converted.end?.usdValue ?? 0) - (converted.start?.usdValue ?? 0),
    gainLossUsd: 0,
  }
}
